import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { MatSidenav } from '@angular/material/sidenav';

import { Item, SidenavConfig } from '@firestitch/sidenav';
import { FsStore } from '@firestitch/store';

import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, takeUntil } from 'rxjs/operators';

import { Account } from '@app/common';
import { AclService } from '@app/core';
import { ProjectSidenavService, SignoutService } from '@app/core/services';

import { SidenavService } from '../../services/sidenav.service';


@Component({
  selector: 'app-page-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent implements OnInit, OnDestroy {

  @ViewChild('sidenav', { static: true })
  public sidenav: MatSidenav;

  @ViewChild('sideNavMainEl', { static: true })
  public sideNavMainEl;

  @ViewChild('notifications', { static: true })
  public notificationsTemplate: TemplateRef<any>;

  public disableClose: boolean;
  public sideNavMain: SidenavConfig = { items: [] };
  public sideNavBottom: SidenavConfig = { items: [] };
  public workspaceSidenavItems = [];
  public unreadNotifications: number;
  public account: Account;

  private _destroy$ = new Subject<void>();

  constructor(
    public sidenavService: SidenavService,
    private _store: FsStore,
    private _router: Router,
    private _aclQueryService: AclService,
    private _signoutService: SignoutService,
    private _cdRef: ChangeDetectorRef,
    private _projectSidenavService: ProjectSidenavService,
  ) {
    this.disableClose = this.sidenavService.mode !== 'over';

    this.sidenavService.resize$
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.disableClose = this.sidenavService.mode !== 'over';
        if (this.sidenavService.opened) {
          this.sidenav.open();
        }
        this._cdRef.markForCheck();
      });

    this._projectSidenavService.project$
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._loadMainSidenav();
      });

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => window.location.pathname),
        distinctUntilChanged(),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._loadMainSidenav();
      });
  }

  public ngOnInit(): void {
    this.account = this._store.get('account');
    this._buildSidenavBottom();

    this._store.observe('permissions')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._buildSidenavBottom();
      });

    this._store.observe('account')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe((store) => {
        this.account = store.value;
        this._buildSidenavBottom();
      });
  }

  public clickNav($event: any): void {
    this.sidenavService.hide();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _loadMainSidenav(): void {
    this.sideNavMain = {
      items: [
        {
          label: 'Sites',
          path: '/sites',
          icon: 'public',
        },
        {
          label: 'Components',
          path: '/',
          icon: 'widgets',
        },
        // In the main rail rather than under System with its siblings: it is
        // the one system screen an operator goes to because something is
        // broken, and a screen you reach by remembering a URL is a screen
        // nobody checks. Gated on the same permission the endpoint is, so it
        // never shows a door that answers Access Denied.
        ...(this._aclQueryService.hasPermissionAdmin()
          ? [{
            label: 'Redis',
            path: '/system/redis',
            icon: 'bolt',
          }]
          : []),
      ],
    };

    this._cdRef.markForCheck();
  }

  private _buildSidenavBottom(): void {
    let items: Item[] = [];
    items = this._appendAccountNav(items);
    items = this._appendAdminNav(items);
    items = this._appendSystemNav(items);
    items = this._appendSignoutNav(items);

    this.sideNavBottom = { items };
    this._cdRef.markForCheck();
  }

  private _appendAccountNav(items: Item[]): Item[] {
    if (this.account) {
      items.push({
        label: `${this.account.name}`,
        superlabel: 'My Account',
        path: '/account',
        image: this.account.avatar.small,
        exact: false,
      });
    }

    return items;
  }

  private _appendAdminNav(items: Item[]): Item[] {
    if (!this._aclQueryService.hasPermissionAdmin()) {
      return items;
    }

    return [
      ...items,
      {
        label: 'Admin',
        path: '/admin',
        icon: 'settings',
        class: 'admin',
        items: [
          {
            label: 'Accounts',
            path: '/admin/accounts',
            exact: false,
          },
          {
            label: 'Messages',
            path: '/admin/message',
            exact: false,
          },
          {
            label: 'Scripts',
            path: '/admin/scripts',
            exact: false,
          },
        ],
      },
    ];
  }

  private _appendSystemNav(items: Item[]): Item[] {
    if (!this._aclQueryService.hasPermissionAdmin()) {
      return items;
    }

    return [
      ...items,
      {
        label: 'System',
        path: '/system',
        icon: 'personal_video',
        items: [
          {
            label: 'Settings',
            path: '/system/settings',
          },
          {
            label: 'Crons',
            path: '/system/crons',
          },
          {
            label: 'Processes',
            path: '/system/processes',
          },
          {
            label: 'Logs',
            path: '/system/logs',
          },
          {
            label: 'Info',
            path: '/system/info',
          },
          {
            label: 'Blueprint',
            path: '/system/blueprint',
            exact: false,
          },
        ],
      },
    ];
  }

  private _appendSignoutNav(items: Item[]): Item[] {
    return [
      ...items,
      {
        label: 'Sign Out',
        icon: 'exit_to_app',
        click: () => {
          this._signoutService.signout();
        },
      },
    ];
  }
}
